import { useEffect, useRef, useState, type InputHTMLAttributes, type JSX, type ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AlertTriangle, CheckCircle2, ChevronDown, Info, XCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Small building blocks the workspace screens share. They compose the portal
 * primitives; nothing here introduces a new visual language.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

/** Single-choice control for 2–4 options, where a dropdown would hide the choice. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  label: string;
  className?: string;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} className={cn('inline-flex h-9 items-center rounded-lg border border-input bg-[var(--input-bg)] p-0.5', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'h-full rounded-md px-3 text-sm font-medium transition-colors duration-[120ms]',
            value === o.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function CheckboxField({
  checked,
  onChange,
  children,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className={cn('flex cursor-pointer items-start gap-2.5 text-sm', disabled && 'cursor-not-allowed opacity-60')}>
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-[var(--brand)]"
        checked={checked}
        disabled={disabled}
        onChange={(ev) => onChange(ev.target.checked)}
      />
      <span className="min-w-0">
        <span className="text-foreground">{children}</span>
        {hint && <span className="mt-0.5 block text-sm text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}

export type NoteTone = 'info' | 'success' | 'warning' | 'error';

const NOTE_STYLES: Record<NoteTone, { cls: string; icon: LucideIcon; iconCls: string }> = {
  info: { cls: 'border-info/30 bg-info/10', icon: Info, iconCls: 'text-info-strong' },
  success: { cls: 'border-success/30 bg-success/10', icon: CheckCircle2, iconCls: 'text-success-strong' },
  warning: { cls: 'border-warning/35 bg-warning/10', icon: AlertTriangle, iconCls: 'text-pending-strong' },
  error: { cls: 'border-destructive/35 bg-destructive/10', icon: XCircle, iconCls: 'text-destructive-strong' },
};

/** Inline status note: an icon, a sentence, and optionally an action on the right. */
export function Note({
  tone = 'info',
  children,
  action,
  className,
  role,
}: {
  tone?: NoteTone;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  role?: 'status' | 'alert';
}): JSX.Element {
  const s = NOTE_STYLES[tone];
  const Icon = s.icon;
  return (
    <div role={role} className={cn('flex items-start gap-2.5 rounded-lg border px-3 py-2 text-sm', s.cls, className)}>
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', s.iconCls)} strokeWidth={2} />
      <div className="min-w-0 flex-1">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export interface MenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  hint?: string;
}

/** A button that opens a menu — "Find creators ▾". */
export function MenuButton({
  label,
  icon: Icon,
  items,
  variant = 'default',
  size = 'sm',
}: {
  label: string;
  icon?: LucideIcon;
  items: readonly MenuItem[];
  variant?: 'default' | 'outline';
  size?: 'sm' | 'default';
}): JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant={variant} size={size}>
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} className={MENU_CONTENT_CLS}>
          {items.map((item) => (
            <DropdownMenu.Item
              key={item.label}
              disabled={item.disabled}
              onSelect={item.onSelect}
              title={item.hint}
              className={MENU_ITEM_CLS}
            >
              <item.icon className="h-3.5 w-3.5 shrink-0" />
              {item.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export const MENU_CONTENT_CLS = cn(
  'z-50 min-w-[11rem] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-overlay',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
  'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
);

export const MENU_ITEM_CLS = cn(
  'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
  'focus:bg-accent focus:text-accent-foreground',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
);

/**
 * Text/number input that commits after a pause (and on Enter/blur), so a
 * server-filtered table isn't refetched on every keystroke.
 */
export function DebouncedInput({
  value,
  onCommit,
  delay = 350,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onCommit: (value: string) => void;
  delay?: number;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  useEffect(() => {
    // An outside change (Clear filters) wins over a stale draft.
    committed.current = value;
    setDraft(value);
  }, [value]);
  useEffect(() => {
    if (draft === committed.current) return;
    const t = setTimeout(() => {
      committed.current = draft;
      onCommit(draft);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  const flush = () => {
    if (draft !== committed.current) {
      committed.current = draft;
      onCommit(draft);
    }
  };
  return (
    <Input
      {...props}
      value={draft}
      onChange={(ev) => setDraft(ev.target.value)}
      onBlur={flush}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') flush();
      }}
    />
  );
}

/** Deterministic folder colour for folders that never had one picked. */
const FOLDER_PALETTE = ['#8EA2FF', '#45D7C4', '#F7BC5E', '#E58BB5', '#7DD3FC', '#A3E635', '#C4B5FD', '#FCA5A5'];

export function folderColor(f: { color: string | null; publicId: string }): string {
  if (f.color) return f.color;
  let h = 0;
  for (let i = 0; i < f.publicId.length; i++) h = (h * 31 + f.publicId.charCodeAt(i)) >>> 0;
  return FOLDER_PALETTE[h % FOLDER_PALETTE.length]!;
}

export function FolderDot({ folder, className }: { folder: { color: string | null; publicId: string }; className?: string }): JSX.Element {
  return <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', className)} style={{ background: folderColor(folder) }} />;
}

/**
 * A labelled filter control. Same layout as the portal's FilterField, with a
 * sentence-case label instead of an uppercase eyebrow.
 */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={cn('grid w-full gap-1 md:w-auto', className)}>
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}
